import React from 'react'
import PropTypes from 'prop-types'
import I18n from 'translations/i18n'
import { Drawer } from '@jisr-hr/design-system'
import { useReactToPrint } from 'react-to-print'
import { isNumber } from 'lodash'
import { getRequestTranslationKey } from 'utils/uiHelpers'
import { useSelector } from 'utils/hooks'

import Request from './Request'
import Footer from './Footer'

import { useRequest } from './RequestProvider'

const RequestDrawer = ({ needApproval, onClose, hideFooter, ...props }) => {
  const [printing, setPrinting] = React.useState(false)
  const componentToPrintRef = React.useRef(null)
  const onBeforeGetContentResolve = React.useRef(null)
  const { request_types } = useSelector(({ applicableRequests }) => applicableRequests)

  const { open, showFooter, drawerToggle, vars, requestType, setRequestType } = useRequest()

  const currentType =
    requestType === 'Request' || isNumber(requestType)
      ? request_types?.find(
          (t) => t.id === (isNumber(requestType) ? requestType : vars.request_type_id),
        )
      : requestType

  const reactToPrintContent = React.useCallback(() => {
    return componentToPrintRef.current
  }, [componentToPrintRef.current])

  const handleOnBeforeGetContent = React.useCallback(() => {
    setPrinting(true)

    return new Promise((resolve) => {
      onBeforeGetContentResolve.current = resolve

      setTimeout(() => {
        setPrinting(false)
        resolve()
      }, 2000)
    })
  }, [setPrinting])

  const headerTitle =
    requestType === 'Request' || isNumber(requestType)
      ? currentType?.name_i18n || vars?.request_type_name
      : I18n.t(getRequestTranslationKey(currentType))

  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: headerTitle || currentType,
    onBeforeGetContent: handleOnBeforeGetContent,
    removeAfterPrint: true,
  })

  React.useEffect(() => {
    if (typeof onBeforeGetContentResolve.current === 'function') {
      onBeforeGetContentResolve.current()
    }
  }, [onBeforeGetContentResolve.current])

  return (
    <Drawer
      active={open}
      {...(!needApproval &&
        requestType &&
        vars.isCreate && {
          onBack: () => {
            setRequestType(null)
          },
        })}
      onClose={() => {
        onClose()
        drawerToggle(false)
      }}
      title={headerTitle || I18n.t('new_request')}
      testId="request_drawer"
      closeIconTestId="close_icon"
      content={
        <Request
          ref={componentToPrintRef}
          readOnly={needApproval}
          requestType={
            requestType === 'Request' || isNumber(requestType)
              ? currentType?.type || vars?.request_type
              : currentType
          }
          customTypeId={currentType?.id}
          {...(!!requestType && { employeeId: vars?.id })}
        />
      }
      footer={
        showFooter && (
          <Footer
            readOnly={needApproval}
            handlePrint={handlePrint}
            printing={printing}
          />
        )
      }
      {...props}
    />
  )
}

RequestDrawer.propTypes = {
  requestType: PropTypes.string,
  needApproval: PropTypes.bool,
  active: PropTypes.bool,
  onClose: PropTypes.func,
  vars: PropTypes.shape(),
  hideFooter: PropTypes.bool,
}

export default RequestDrawer
